import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/feature/Layout';
import { fetchVoices } from '../../repositories/voices';
import type { Voice } from '../../types/voices';
import { useSEO, generateWebPageSchema } from '../../utils/seo';

export default function VoicesPage() {
  const navigate = useNavigate();
  const [voices, setVoices] = useState<Voice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // フィルター用のstate
  const [selectedCategory, setSelectedCategory] = useState<string>('すべて');
  const [selectedGrade, setSelectedGrade] = useState<string>('すべて');
  const [keyword, setKeyword] = useState<string>('');

  // SEO設定
  useSEO({
    title: '居場所の悩み・モヤモヤの声｜キョウコレ',
    description: '全国の居場所スタッフや支援者から届いた、日々の悩みや気づきを共有するページです。カテゴリ・学年別に検索できます。',
    keywords: '居場所の悩み,支援者の悩み,学習支援,人間関係,進路,家庭,学校,人手不足',
    canonical: (import.meta.env.VITE_SITE_URL || 'https://example.com') + '/voices',
    jsonLd: generateWebPageSchema(
      '居場所の悩み・モヤモヤの声｜キョウコレ',
      '全国の居場所スタッフや支援者から届いた、日々の悩みや気づきを共有するページです。カテゴリ・学年別に検索できます。',
      '/voices'
    ),
  });

  useEffect(() => {
    loadVoices();
  }, [selectedCategory, selectedGrade, keyword]);

  const loadVoices = async () => {
    try {
      setLoading(true);
      const filters: { category?: string; grade?: string; keyword?: string } = {};
      
      if (selectedCategory !== 'すべて') {
        filters.category = selectedCategory;
      }
      if (selectedGrade !== 'すべて') {
        filters.grade = selectedGrade;
      }
      if (keyword.trim()) {
        filters.keyword = keyword.trim();
      }

      const data = await fetchVoices(filters);
      setVoices(data);
    } catch (err) {
      console.error('Failed to load voices:', err);
      setError('居場所の悩みの読み込みに失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  // カテゴリの一覧
  const categories = ['すべて', '人間関係', '進路', '家庭', '学校', '人手不足', '学習支援', '保護者対応', '子どもの状況', 'お金のこと', 'その他'];

  // 学年の一覧
  const grades = ['すべて', '小1', '小2', '小3', '小4', '小5', '小6', '中1', '中2', '中3', '高校生', 'その他'];

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getExcerpt = (text: string | null, maxLength = 100) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <Layout>
      {/* ヒーローセクション */}
      <div className="bg-gradient-to-br from-purple-50 to-pink-50 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            居場所の悩み・モヤモヤの声
          </h1>
          <p className="text-lg text-gray-700 mb-6">
            全国の居場所スタッフや支援者から届いた、日々の悩みや気づきを共有するページです。
          </p>
          <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 max-w-2xl mx-auto">
            <p className="text-sm text-gray-600 mb-2">
              <i className="ri-information-line mr-1"></i>
              個人が特定されないよう配慮して編集しています
            </p>
            <p className="text-sm text-gray-600">
              <i className="ri-alert-line mr-1"></i>
              深刻な悩みや専門的な相談は、適切な専門機関にご相談ください
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* フィルターエリア */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            <i className="ri-filter-line mr-2"></i>
            絞り込み検索
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* カテゴリフィルター */}
            <div>
              <label htmlFor="category-filter" className="block text-sm font-medium text-gray-700 mb-2">
                カテゴリ
              </label>
              <select
                id="category-filter"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 cursor-pointer"
              >
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>

            {/* 学年フィルター */}
            <div>
              <label htmlFor="grade-filter" className="block text-sm font-medium text-gray-700 mb-2">
                学年・年代
              </label>
              <select
                id="grade-filter"
                value={selectedGrade}
                onChange={(e) => setSelectedGrade(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 cursor-pointer"
              >
                {grades.map((grade) => (
                  <option key={grade} value={grade}>
                    {grade}
                  </option>
                ))}
              </select>
            </div>

            {/* キーワード検索 */}
            <div>
              <label htmlFor="keyword-search" className="block text-sm font-medium text-gray-700 mb-2">
                キーワード検索
              </label>
              <input
                id="keyword-search"
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="タイトル・内容で検索"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>
        </div>

        {/* ローディング */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mb-4"></div>
            <p className="text-gray-600">読み込み中…</p>
          </div>
        )}

        {/* エラー */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* 結果なし */}
        {!loading && !error && voices.length === 0 && (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <i className="ri-inbox-line text-6xl text-gray-300 mb-4"></i>
            <p className="text-gray-600 text-lg mb-2">
              条件に一致する悩みが見つかりませんでした
            </p>
            <p className="text-sm text-gray-500">
              フィルターを変更して再度お試しください
            </p>
          </div>
        )}

        {/* 悩み一覧 */}
        {!loading && !error && voices.length > 0 && (
          <>
            <div className="mb-4 text-sm text-gray-600">
              {voices.length}件の悩みが見つかりました
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {voices.map((voice) => (
                <div
                  key={voice.id}
                  onClick={() => navigate(`/voices/${voice.slug || voice.id}`)}
                  className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer"
                >
                  {/* ピックアップバッジ */}
                  {voice.is_featured && (
                    <div className="mb-3">
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded whitespace-nowrap">
                        <i className="ri-star-fill mr-1"></i>
                        ピックアップ
                      </span>
                    </div>
                  )}

                  {/* メタ情報 */}
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    {voice.category && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded whitespace-nowrap">
                        {voice.category}
                      </span>
                    )}
                    {voice.grade && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded whitespace-nowrap">
                        {voice.grade}
                      </span>
                    )}
                    {voice.place_type && (
                      <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded whitespace-nowrap">
                        {voice.place_type}
                      </span>
                    )}
                  </div>

                  {/* タイトル */}
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                    {voice.title}
                  </h3>

                  {/* 本文抜粋 */}
                  <p className="text-sm text-gray-600 mb-3 line-clamp-3">
                    {getExcerpt(voice.body_public || voice.body_raw || '', 80)}
                  </p>

                  {/* タグ */}
                  {voice.tags && voice.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {voice.tags.slice(0, 3).map((tag, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded whitespace-nowrap"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* 投稿日時 */}
                  <div className="flex items-center justify-between text-xs text-gray-500 pt-3 border-t border-gray-100">
                    <span>
                      <i className="ri-calendar-line mr-1"></i>
                      {formatDate(voice.created_at)}
                    </span>
                    {voice.admin_comment && (
                      <span className="text-purple-600 font-medium">
                        <i className="ri-chat-check-line mr-1"></i>
                        コメントあり
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* 専門相談窓口への導線 */}
        <div className="mt-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg p-8 text-white text-center">
          <h2 className="text-2xl font-bold mb-3">
            専門的な相談が必要な場合
          </h2>
          <p className="text-lg mb-6 opacity-90">
            深刻な悩みや専門的なサポートが必要な場合は、適切な専門機関にご相談ください。
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="https://www.mhlw.go.jp/stf/seisakunitsuite/bunya/hukushi_kaigo/seikatsuhogo/jisatsu/soudan_tel.html"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-3 bg-white text-purple-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line mr-2"></i>
              相談窓口一覧
            </a>
          </div>
        </div>
      </div>
    </Layout>
  );
}
